import { create } from 'zustand'
import { TextFile, PreprocessingResult, CompleteMicroSegmentationResult, LLMConfig } from './segmentationStore'
import type { AlignmentResult, SceneLayer } from '../types/segmentationTypes'
import { dbStorage, initializeDB } from '../lib/indexedDBStorage'
import { buildSourceObjectMatchKey, buildSourceObjectRecord } from '../lib/sourceObjectIdentity'
import {
  BookResumeData,
  ChapterResumeData,
  WitnessResumeSession,
  BatchResumeSession
} from '../types/resumeTypes'
import { resumeStorage } from '../lib/resumeStorage'
import { syncManager } from '../lib/syncManager'
import type { SourceObject } from '../types/sourceObjects'
import { normalizeSegmentationResult } from '../utils/segmentationNormalization'
import { deriveContextLabelPrefix } from '../lib/contextLabelPrefix'
import { syncWitnessSegmentationToAnchoredRows } from '../lib/witnessLedgerSync'
import { buildCascadeScope, executeCascade, previewCascade } from '../lib/cascadeDelete'
import type { CascadePlan, CascadeTarget } from '../lib/cascadeDelete'
// Debug logging helper
const debugLog = (message: string, data?: any) => {
  if (process.env.NODE_ENV === 'development') {
    console.log(`[BooksStore] ${message}`, data || '')
  }
}


const getChunkId = (chunk: any, fallback: string) => {
  if (!chunk) return fallback
  return chunk.source_chunk_id || chunk.chunk_id || fallback
}

const mergeSegmentationResults = (existing: any, incoming: any) => {
  if (!incoming || !Array.isArray(incoming.chunk_results)) {
    return existing
  }

  if (!existing || !Array.isArray(existing.chunk_results)) {
    return normalizeSegmentationResult(incoming)
  }

  const existingChunks = existing.chunk_results || []
  const incomingChunks = incoming.chunk_results || []

  const incomingMap = new Map<string, any>()
  incomingChunks.forEach((chunk: any, index: number) => {
    const id = getChunkId(chunk, `incoming_${index}`)
    if (!chunk.source_chunk_id && chunk.chunk_id) {
      chunk.source_chunk_id = chunk.chunk_id
    }
    incomingMap.set(id, chunk)
  })

  const mergedChunks: any[] = []
  const seenIds = new Set<string>()

  existingChunks.forEach((chunk: any, index: number) => {
    const id = getChunkId(chunk, `existing_${index}`)
    if (incomingMap.has(id)) {
      mergedChunks.push(incomingMap.get(id))
      seenIds.add(id)
    } else {
      mergedChunks.push(chunk)
      seenIds.add(id)
    }
  })

  incomingMap.forEach((chunk, id) => {
    if (!seenIds.has(id)) {
      mergedChunks.push(chunk)
      seenIds.add(id)
    }
  })

  const mergedMetadata = {
    ...(existing.segmentation_metadata || {}),
    ...(incoming.segmentation_metadata || {})
  }

  const pendingEntries: Array<{ chunk_id: string; origin?: string; start_position?: number; end_position?: number }> = []
  const pendingIds: string[] = []

  mergedChunks.forEach((chunk: any, index: number) => {
    const id = getChunkId(chunk, `merged_${index}`)
    const hasUnits = Array.isArray(chunk.micro_units) && chunk.micro_units.length > 0
    const status = chunk.processing_status || (hasUnits ? 'success' : 'pending')
    if (!hasUnits || (status && status !== 'success')) {
      pendingIds.push(id)
      const metadata = chunk.processing_metadata || {}
      pendingEntries.push({
        chunk_id: id,
        origin: metadata.origin,
        start_position: metadata.start_position,
        end_position: metadata.end_position
      })
    }
  })

  mergedMetadata.pending_chunks = pendingIds
  mergedMetadata.pending_manifest = pendingEntries

  const mergedAggregated = {
    ...(existing.aggregated_statistics || {}),
    ...(incoming.aggregated_statistics || {}),
    total_chunks: mergedChunks.length,
    total_units: mergedChunks.reduce((sum: number, chunk: any) => sum + (Array.isArray(chunk.micro_units) ? chunk.micro_units.length : 0), 0)
  }

  const mergedResult = {
    ...existing,
    ...incoming,
    chunk_results: mergedChunks,
    segmentation_metadata: mergedMetadata,
    aggregated_statistics: mergedAggregated,
    id_remap: existing?.id_remap || incoming?.id_remap ? { ...(existing?.id_remap || {}), ...(incoming?.id_remap || {}) } : undefined,
    numbers: existing?.numbers || incoming?.numbers ? { ...(existing?.numbers || {}), ...(incoming?.numbers || {}) } : undefined
  }

  return normalizeSegmentationResult(mergedResult)
}

/**
 * Validate segmentation result integrity against the original source text.
 * Checks that each unit's text content matches what's at its claimed position
 * in the original uploaded file.
 * 
 * @returns Array of warning messages for any mismatches found
 */
const validateSegmentationIntegrity = (
  segmentationResult: CompleteMicroSegmentationResult | null | undefined,
  sourceText: string | undefined,
  witnessId: string
): string[] => {
  const warnings: string[] = []

  if (!segmentationResult?.chunk_results || !sourceText) {
    return warnings
  }

  let violationCount = 0
  const MAX_WARNINGS = 5

  for (const chunk of segmentationResult.chunk_results) {
    for (const unit of chunk.micro_units || []) {
      const start = unit.start_position
      const end = unit.end_position
      const unitText = unit.unit_text_content || ''

      // Skip units without valid positions
      if (start === undefined || end === undefined || start < 0) continue

      // Extract text from source at claimed position
      const expectedText = sourceText.substring(start, end)

      // Normalize for comparison (trim whitespace)
      const normalizedUnit = unitText.trim()
      const normalizedExpected = expectedText.trim()

      // Check if they match
      if (normalizedUnit !== normalizedExpected) {
        violationCount++

        if (violationCount <= MAX_WARNINGS) {
          warnings.push(
            `Unit ${unit.unit_id} text mismatch at position ${start}: ` +
            `expected "${normalizedExpected.slice(0, 30)}..." but got "${normalizedUnit.slice(0, 30)}..."`
          )
        }
      }
    }
  }

  if (violationCount > MAX_WARNINGS) {
    warnings.push(`...and ${violationCount - MAX_WARNINGS} more text-position mismatches`)
  }

  if (warnings.length > 0) {
    console.warn(`⚠️ [INTEGRITY] Witness ${witnessId}: ${violationCount} text-position mismatches detected`, warnings)
  }

  return warnings
}

const buildWitnessProcessingUpdates = ({
  witness,
  type,
  normalizedResult
}: {
  witness: Witness | undefined
  type: 'preprocessing' | 'segmentation'
  normalizedResult: any
}): Partial<Witness> => {
  const updateField = type === 'preprocessing' ? 'preprocessing_result' : 'segmentation_result'

  let finalResult = normalizedResult
  let witnessUpdates: Partial<Witness> = { [updateField]: finalResult } as Partial<Witness>

  if (type === 'segmentation') {
    // Preserve previously saved scene layers unless the incoming payload explicitly provides one
    const existingScenes = witness?.segmentation_result?.scenes
    const incomingScenes = normalizedResult?.scenes
    const mergedScenes = incomingScenes ?? existingScenes

    if (mergedScenes) {
      const totalScenes = Array.isArray(mergedScenes.scene_order)
        ? mergedScenes.scene_order.length
        : Array.isArray(mergedScenes.scene_map)
          ? mergedScenes.scene_map.length
          : 0

      finalResult = {
        ...normalizedResult,
        scenes: mergedScenes,
        aggregated_statistics: {
          ...(normalizedResult?.aggregated_statistics || {}),
          total_scenes: totalScenes
        }
      }
    }

    witnessUpdates = { [updateField]: finalResult } as Partial<Witness>

    // Keep active segmentation run synchronized with manual/override updates.
    const activeRunId = witness?.active_segmentation_run
    if (activeRunId && witness?.segmentation_runs?.[activeRunId]) {
      const activeRun = witness.segmentation_runs[activeRunId]
      const totalChunks = Array.isArray(finalResult?.chunk_results)
        ? finalResult.chunk_results.length
        : activeRun.totalChunks
      const totalUnits = Array.isArray(finalResult?.chunk_results)
        ? finalResult.chunk_results.reduce(
          (sum: number, chunk: any) => sum + (Array.isArray(chunk.micro_units) ? chunk.micro_units.length : 0),
          0
        )
        : activeRun.totalUnits

      witnessUpdates.segmentation_runs = {
        ...witness.segmentation_runs,
        [activeRunId]: {
          ...activeRun,
          result: finalResult,
          totalChunks,
          totalUnits
        }
      }
    }
  }

  return witnessUpdates
}

// Core interfaces for the scholarly book workflow
export interface Book {
  id: string
  name: string
  arabic_title?: string          // Original-script title shown alongside the Latin name
  authority_work_id?: string     // Optional pointer to an AuthorityWork record (no migration implied)
  description?: string
  archived?: boolean             // Phase B: frozen held container, hidden from active desk views
  archived_at?: number
  created_at: number
  last_modified: number
  chapter_order: string[]        // Ordered array of chapter IDs
}

export interface Chapter {
  id: string
  book_id: string
  name: string
  label_prefix?: string
  description?: string
  authority_work_id?: string     // Optional pointer to an AuthorityWork (for compilations whose chapters belong to different works)
  canonical_section_id?: string  // Optional pointer to an AuthoritySection of the assigned work
  archived?: boolean             // Phase B: frozen duplicate chapter, preserved but hidden by default
  archived_at?: number
  order_index: number            // For manual ordering
  created_at: number
}

export interface SegmentationRun {
  runId: string
  createdAt: number
  completedAt?: number
  llmConfig: LLMConfig
  totalChunks: number
  totalUnits: number
  processingTime?: number
  status: 'processing' | 'completed' | 'error' | 'partial'
  result: CompleteMicroSegmentationResult
}

export interface Witness {
  id: string
  chapter_id: string
  book_id: string                // SECURITY: Add book_id for proper isolation
  source_object_id?: string
  sigla: string                  // MS identifier (e.g., "A", "B1", "Par123")
  description: string            // Full manuscript description
  manuscript_date?: string       // Dating information (e.g., "13th century", "ca. 1250")
  copy_year?: number             // Numeric copy date for chronological displays (e.g., 1250)
  library: string               // Holding institution (e.g., "Bibliothèque nazionale de France")
  city?: string
  shelfmark?: string
  language?: string
  witness_type?: 'manuscript' | 'print' | 'edition' | 'other'
  transcription_type?: 'diplomatic' | 'lightly_normalized' | 'normalized' | 'unknown'
  coverage_status?: 'complete' | 'partial' | 'excerpt' | 'unknown'
  lacunose_status?: 'not_lacunose' | 'lacunose' | 'unknown'
  uploaded_file: TextFile
  preprocessing_result?: PreprocessingResult
  segmentation_result?: CompleteMicroSegmentationResult  // Legacy: keep for backward compatibility
  segmentation_runs?: Record<string, SegmentationRun>     // New: multiple runs
  active_segmentation_run?: string                         // Currently selected run
  processing_status: 'pending' | 'preprocessing' | 'preprocessed' | 'segmenting' | 'auto-processing' | 'completed' | 'error' | 'paused'
  created_at: number
  last_modified: number          // SYNC: For change detection
  version: number               // SYNC: Version for synchronization
}

export interface WitnessProcessingResultUpdate {
  witnessId: string
  type: 'preprocessing' | 'segmentation'
  result: any
  status?: Witness['processing_status']
}

export const prepareWitnessProcessingResultRecord = ({
  witness,
  chapterBookId,
  update,
  now = Date.now(),
}: {
  witness: Witness
  chapterBookId?: string
  update: WitnessProcessingResultUpdate
  now?: number
}): Witness => {
  const book_id = witness.book_id || chapterBookId
  if (!book_id) {
    throw new Error('Missing book_id - security validation failed')
  }

  const normalizedResult = normalizeSegmentationResult(update.result)
  const witnessUpdates = buildWitnessProcessingUpdates({
    witness,
    type: update.type,
    normalizedResult,
  })

  if (update.status) {
    witnessUpdates.processing_status = update.status
  }
  if (update.type === 'segmentation' && witnessUpdates.segmentation_result) {
    validateSegmentationIntegrity(
      witnessUpdates.segmentation_result as any,
      witness.uploaded_file?.content,
      witness.id,
    )
  }

  return {
    ...witness,
    ...witnessUpdates,
    book_id,
    last_modified: now,
    version: (witness.version || 0) + 1,
  }
}

export interface ChapterAlignmentSession {
  id: string
  chapter_id: string
  book_id: string                // SECURITY: Add book_id for proper isolation
  selected_witnesses: string[]   // witness IDs
  alignment_result?: AlignmentResult
  created_at: number
}

// Export data structure for sharing between users
export interface BookExport {
  export_type: 'scholarly_book_v1'
  export_timestamp: number
  book: Book
  chapters: Chapter[]
  witnesses: Witness[]
  segmentation_results: Record<string, CompleteMicroSegmentationResult>
  alignments: ChapterAlignmentSession[]
}

type SourceObjectLinkInput = Partial<Pick<
  Witness,
  'sigla' | 'description' | 'manuscript_date' | 'library' | 'city' | 'shelfmark' | 'witness_type'
>> & Partial<Pick<
  SourceObject,
  'manuscript_record_id' | 'canonical_label' | 'copy_year' | 'source_type' | 'external_catalog_id' | 'provenance'
>>

const resolveOrCreateSourceObjectId = async ({
  workId,
  sourceData,
  existingSourceObjects,
  onStoredSourceObject,
  onCreatedSourceObject
}: {
  workId: string
  sourceData: SourceObjectLinkInput
  existingSourceObjects: SourceObject[]
  onStoredSourceObject: (sourceObject: SourceObject) => void
  onCreatedSourceObject: (sourceObject: SourceObject) => Promise<void>
}): Promise<string> => {
  if (sourceData.manuscript_record_id) {
    const cached = existingSourceObjects.find(sourceObject => (
      sourceObject.work_id === workId && sourceObject.manuscript_record_id === sourceData.manuscript_record_id
    ))
    if (cached) {
      return cached.id
    }

    const storedForWork = await dbStorage.getSourceObjectsByWork(workId)
    const stored = storedForWork.find(sourceObject => sourceObject.manuscript_record_id === sourceData.manuscript_record_id)
    if (stored) {
      onStoredSourceObject(stored)
      return stored.id
    }
  }

  const matchKey = buildSourceObjectMatchKey(sourceData)

  if (matchKey) {
    const cached = existingSourceObjects.find(sourceObject => (
      sourceObject.work_id === workId && sourceObject.normalized_match_key === matchKey
    ))
    if (cached) {
      return cached.id
    }

    const stored = await dbStorage.getSourceObjectByMatchKey(workId, matchKey)
    if (stored) {
      onStoredSourceObject(stored)
      return stored.id
    }
  }

  const createdAt = Date.now()
  const sourceObject = buildSourceObjectRecord({
    id: generateSecureId(),
    workId,
    input: sourceData,
    createdAt
  })
  if (sourceData.manuscript_record_id) {
    sourceObject.manuscript_record_id = sourceData.manuscript_record_id
  }
  if (sourceData.canonical_label) {
    sourceObject.canonical_label = sourceData.canonical_label
  }
  if (typeof sourceData.copy_year === 'number') {
    sourceObject.copy_year = sourceData.copy_year
  }
  if (sourceData.source_type) {
    sourceObject.source_type = sourceData.source_type
  }
  if (sourceData.external_catalog_id) {
    sourceObject.external_catalog_id = sourceData.external_catalog_id
  }
  if (sourceData.provenance) {
    sourceObject.provenance = sourceData.provenance
  }
  await onCreatedSourceObject(sourceObject)
  return sourceObject.id
}

const mergeSourceObjectCollection = (
  sourceObjects: SourceObject[],
  sourceObject: SourceObject
): SourceObject[] => (
  sourceObjects.some(candidate => candidate.id === sourceObject.id)
    ? sourceObjects
    : [...sourceObjects, sourceObject]
)

const backfillWitnessSourceObjects = async ({
  witnesses,
  sourceObjects
}: {
  witnesses: Witness[]
  sourceObjects: SourceObject[]
}): Promise<{
  witnesses: Witness[]
  sourceObjects: SourceObject[]
  report: {
    linkedWitnesses: number
    repairedWitnesses: number
    createdSourceObjects: number
  }
}> => {
  const hydratedWitnesses = [...witnesses]
  let hydratedSourceObjects = [...sourceObjects]
  let linkedWitnesses = 0
  let repairedWitnesses = 0
  let createdSourceObjects = 0

  for (let index = 0; index < hydratedWitnesses.length; index += 1) {
    const witness = hydratedWitnesses[index]
    let linkedSourceObject = witness.source_object_id
      ? hydratedSourceObjects.find(sourceObject => sourceObject.id === witness.source_object_id) || null
      : null

    if (!linkedSourceObject && witness.source_object_id) {
      linkedSourceObject = await dbStorage.getSourceObject(witness.source_object_id)
      if (linkedSourceObject) {
        hydratedSourceObjects = mergeSourceObjectCollection(hydratedSourceObjects, linkedSourceObject)
      }
    }

    if (linkedSourceObject) {
      continue
    }

    const matchKey = buildSourceObjectMatchKey(witness)
    let sourceObjectId: string

    if (matchKey) {
      const cachedSourceObject = hydratedSourceObjects.find(sourceObject => (
        sourceObject.work_id === witness.book_id && sourceObject.normalized_match_key === matchKey
      ))
      if (cachedSourceObject) {
        sourceObjectId = cachedSourceObject.id
      } else {
        const storedSourceObject = await dbStorage.getSourceObjectByMatchKey(witness.book_id, matchKey)
        if (storedSourceObject) {
          hydratedSourceObjects = mergeSourceObjectCollection(hydratedSourceObjects, storedSourceObject)
          sourceObjectId = storedSourceObject.id
        } else {
          const createdAt = Date.now()
          const sourceObject = buildSourceObjectRecord({
            id: generateSecureId(),
            workId: witness.book_id,
            input: witness,
            createdAt
          })
          await dbStorage.saveSourceObject(sourceObject)
          hydratedSourceObjects = mergeSourceObjectCollection(hydratedSourceObjects, sourceObject)
          createdSourceObjects += 1
          sourceObjectId = sourceObject.id
        }
      }
    } else {
      const createdAt = Date.now()
      const sourceObject = buildSourceObjectRecord({
        id: generateSecureId(),
        workId: witness.book_id,
        input: witness,
        createdAt
      })
      await dbStorage.saveSourceObject(sourceObject)
      hydratedSourceObjects = mergeSourceObjectCollection(hydratedSourceObjects, sourceObject)
      createdSourceObjects += 1
      sourceObjectId = sourceObject.id
    }

    const updatedWitness: Witness = {
      ...witness,
      source_object_id: sourceObjectId,
      last_modified: Date.now(),
      version: (witness.version || 0) + 1
    }

    await dbStorage.put('witnesses', updatedWitness)
    hydratedWitnesses[index] = updatedWitness

    if (witness.source_object_id) {
      repairedWitnesses += 1
    } else {
      linkedWitnesses += 1
    }
  }

  return {
    witnesses: hydratedWitnesses,
    sourceObjects: hydratedSourceObjects,
    report: {
      linkedWitnesses,
      repairedWitnesses,
      createdSourceObjects
    }
  }
}

interface BooksState {
  // Core data
  books: Book[]
  chapters: Chapter[]  // Cached chapters for synchronous access
  witnesses: Witness[] // Cached witnesses for synchronous access
  sourceObjects: SourceObject[]
  currentBookId: string | null
  currentChapterId: string | null

  // Resume session data
  bookResumeData: Map<string, BookResumeData>     // bookId -> resume data
  chapterResumeData: Map<string, ChapterResumeData> // chapterId -> resume data
  witnessResumeSessions: Map<string, WitnessResumeSession> // witnessId -> session
  batchResumeSessions: Map<string, BatchResumeSession>     // batchId -> session

  // Loading states
  loading: boolean
  error: string | null
  dbInitialized: boolean
  resumeDataLoading: boolean

  // Book management actions
  createBook: (name: string, description?: string, arabicTitle?: string) => Promise<string>
  updateBook: (bookId: string, updates: Partial<Omit<Book, 'id' | 'created_at'>>) => Promise<void>
  archiveBook: (bookId: string, archivedAt?: number) => Promise<void>
  restoreBook: (bookId: string) => Promise<void>
  deleteBook: (bookId: string) => void

  // Cascade deletion: one held work / chapter / witness plus every row it
  // owns across the chapter- and witness-scoped IndexedDB stores.
  previewDeletion: (target: CascadeTarget) => Promise<CascadePlan>
  deleteCascade: (target: CascadeTarget) => Promise<CascadePlan>
  setCurrentBook: (bookId: string | null) => void
  reorderChapters: (bookId: string, newOrder: string[]) => void

  // Chapter management actions
  createChapter: (bookId: string, name: string, description?: string) => Promise<string>
  updateChapter: (chapterId: string, updates: Partial<Omit<Chapter, 'id' | 'book_id' | 'created_at'>>) => Promise<void>
  archiveChapter: (chapterId: string, archivedAt?: number) => Promise<void>
  restoreChapter: (chapterId: string) => Promise<void>
  deleteChapter: (chapterId: string) => void
  setCurrentChapter: (chapterId: string | null) => void
  moveChapter: (chapterId: string, direction: 'up' | 'down') => void

  // Witness management actions
  createWitness: (chapterId: string, witnessData: Omit<Witness, 'id' | 'chapter_id' | 'book_id' | 'created_at' | 'processing_status'>) => Promise<string>
  ensureRecordSourceObjectLink: (workId: string, sourceData: SourceObjectLinkInput) => Promise<string>
  ensureWitnessSourceObjectLink: (witnessId: string, sourceData?: SourceObjectLinkInput) => Promise<string | null>
  updateSourceObject: (sourceObjectId: string, updates: Partial<Omit<SourceObject, 'id' | 'work_id' | 'created_at'>>) => Promise<void>
  updateWitness: (witnessId: string, updates: Partial<Omit<Witness, 'id' | 'chapter_id' | 'book_id' | 'created_at'>>) => Promise<void>
  refreshWitness: (witnessId: string) => Promise<void>
  deleteWitness: (witnessId: string) => void
  updateWitnessProcessingResult: (witnessId: string, type: 'preprocessing' | 'segmentation', result: any) => Promise<void>
  updateWitnessProcessingResultsBatch: (updates: Array<{
    witnessId: string
    type: 'preprocessing' | 'segmentation'
    result: any
    status?: Witness['processing_status']
  }>) => Promise<void>
  mergeWitnessSegmentationResult: (witnessId: string, partialResult: CompleteMicroSegmentationResult) => Promise<void>
  updateWitnessScenes: (witnessId: string, sceneLayer: SceneLayer) => Promise<void>
  updateWitnessChunkResult: (witnessId: string, chunkId: string, result: any) => Promise<void>
  updateWitnessStatus: (witnessId: string, status: Witness['processing_status']) => Promise<void>

  // Multiple segmentation run management
  createSegmentationRun: (witnessId: string, runId: string, llmConfig: LLMConfig) => Promise<void>
  updateSegmentationRun: (witnessId: string, runId: string, updates: Partial<SegmentationRun>) => Promise<void>
  completeSegmentationRun: (witnessId: string, runId: string, result: CompleteMicroSegmentationResult) => Promise<void>
  deleteSegmentationRun: (witnessId: string, runId: string) => Promise<void>
  setActiveSegmentationRun: (witnessId: string, runId: string | undefined) => Promise<void>
  getWitnessSegmentationRuns: (witnessId: string) => SegmentationRun[]

  // Getter functions
  getBook: (bookId: string) => Book | undefined
  getChapter: (chapterId: string) => Chapter | undefined
  getWitness: (witnessId: string) => Witness | undefined
  getSourceObject: (sourceObjectId: string) => SourceObject | undefined
  getWorkSourceObjects: (bookId: string) => SourceObject[]
  getBookChapters: (bookId: string) => Chapter[]
  getChapterWitnesses: (chapterId: string) => Witness[]
  getCurrentBook: () => Book | undefined
  getCurrentChapter: () => Chapter | undefined

  // Note: localStorage functions removed - using IndexedDB only

  // Import/Export functionality
  exportBook: (bookId: string) => BookExport | null
  importBook: (exportData: BookExport) => Promise<string | null>

  // Storage management
  getStorageUsage: () => Promise<{ used: number; estimatedPercentage: number }>
  clearAllData: () => Promise<void>
  initializeDB: () => Promise<void>
  migrateFromLocalStorage: () => Promise<{ success: boolean; migrated: number; errors: string[] }>

  // Alignment management
  createAlignmentSession: (chapterId: string, witnessIds: string[]) => string
  updateAlignmentResult: (sessionId: string, result: AlignmentResult) => void
  getChapterAlignments: (chapterId: string) => ChapterAlignmentSession[]
  deleteAlignmentSession: (sessionId: string) => Promise<boolean>

  // Resume session management
  loadBookResumeData: (bookId: string) => Promise<void>
  loadChapterResumeData: (chapterId: string) => Promise<void>
  loadWitnessResumeSession: (witnessId: string) => Promise<void>
  loadBatchResumeSession: (batchId: string) => Promise<void>

  // Resume session getters
  getBookResumeData: (bookId: string) => BookResumeData | null
  getChapterResumeData: (chapterId: string) => ChapterResumeData | null
  getWitnessResumeSession: (witnessId: string) => WitnessResumeSession | null
  getBatchResumeSession: (batchId: string) => BatchResumeSession | null

  // Resume session actions
  updateWitnessResumeSession: (session: WitnessResumeSession) => Promise<void>
  updateBatchResumeSession: (session: BatchResumeSession) => Promise<void>
  deleteWitnessResumeSession: (witnessId: string) => Promise<void>
  deleteBatchResumeSession: (batchId: string) => Promise<void>

  // Resume utilities
  refreshResumeData: () => Promise<void>
  cleanupExpiredResumeSessions: () => Promise<void>
  getExpiringSessions: (daysUntilExpiry?: number) => Promise<(WitnessResumeSession | BatchResumeSession)[]>
  resumeAllSessions: (bookId: string) => Promise<void>

  // SECURITY: ID migration functions
  detectLegacyWitnesses: () => Witness[]
  migrateAllLegacyWitnessIds: () => Promise<{ migrated: number; errors: string[] }>
  validateAllWitnessIds: () => { valid: number; invalid: number; legacy: number; details: any[] }

  // Sync manager methods
  loadChapterData: (book_id: string, chapter_id: string) => Promise<void>
  loadWitnesses: (chapter_id: string) => Promise<void>

  // WebSocket connections
  processingConnections: Record<string, WebSocket>
  editingConnections: Record<string, WebSocket>
}

// Note: localStorage helper functions removed - now using IndexedDB only

// ============================================================================
// CRITICAL SECURITY: Globally Unique ID Generation System
// ============================================================================

/**
 * SECURITY VULNERABILITY FIX - WITNESS ID COLLISION PREVENTION
 * 
 * CRITICAL: The previous weak timestamp-based ID system caused data leakage
 * between chapters. This new system ensures globally unique IDs with proper
 * book/chapter scoping to maintain academic data integrity.
 */

// Generate cryptographically secure UUID v4
const generateSecureUUID = (): string => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }

  // Fallback for older browsers - still cryptographically secure
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = Math.random() * 16 | 0
    const v = c == 'x' ? r : (r & 0x3 | 0x8)
    return v.toString(16)
  })
}

// Generate scoped witness ID with book/chapter context
const generateWitnessId = (bookId: string, chapterId: string): string => {
  const uuid = generateSecureUUID()
  return `${bookId}_${chapterId}_witness_${uuid}`
}

// Generate secure generic ID for books and chapters
const generateSecureId = (): string => {
  return generateSecureUUID()
}

// Legacy ID generation for backward compatibility (marked as deprecated)
const generateLegacyId = () => `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`

type ArchivableRecord = { archived?: boolean; archived_at?: number }

const withoutArchiveState = <T extends ArchivableRecord>(record: T): T => {
  const { archived: _archived, archived_at: _archivedAt, ...rest } = record
  return rest as T
}

// ============================================================================
// ID Validation and Parsing Functions
// ============================================================================

export const validateWitnessId = (witnessId: string): { isValid: boolean; reason?: string } => {
  if (!witnessId || typeof witnessId !== 'string') {
    return { isValid: false, reason: 'Invalid witness ID format' }
  }

  // Check for new secure format: {bookId}_{chapterId}_witness_{uuid}
  const secureFormatMatch = witnessId.match(/^[a-f0-9-]{36}_[a-f0-9-]{36}_witness_[a-f0-9-]{36}$/)
  if (secureFormatMatch) {
    return { isValid: true }
  }

  // Check for legacy format (for migration purposes)
  const legacyFormatMatch = witnessId.match(/^\d+_[a-z0-9]{9}$/)
  if (legacyFormatMatch) {
    return { isValid: true, reason: 'Legacy format - requires migration' }
  }

  return { isValid: false, reason: 'Unknown witness ID format' }
}

export const parseWitnessId = (witnessId: string): {
  bookId?: string;
  chapterId?: string;
  uuid?: string;
  isLegacy: boolean;
  isValid: boolean;
} => {
  const validation = validateWitnessId(witnessId)
  if (!validation.isValid) {
    return { isLegacy: false, isValid: false }
  }

  // Parse secure format
  const secureFormatMatch = witnessId.match(/^([a-f0-9-]{36})_([a-f0-9-]{36})_witness_([a-f0-9-]{36})$/)
  if (secureFormatMatch) {
    return {
      bookId: secureFormatMatch[1],
      chapterId: secureFormatMatch[2],
      uuid: secureFormatMatch[3],
      isLegacy: false,
      isValid: true
    }
  }

  // Parse legacy format
  return { isLegacy: true, isValid: true }
}

export const verifyWitnessOwnership = (witnessId: string, expectedBookId: string, expectedChapterId: string): boolean => {
  const parsed = parseWitnessId(witnessId)
  if (!parsed.isValid || parsed.isLegacy) {
    // Legacy IDs cannot be verified for ownership - require migration
    return false
  }

  return parsed.bookId === expectedBookId && parsed.chapterId === expectedChapterId
}

// ============================================================================
// Migration Support Functions
// ============================================================================

export const detectLegacyWitnessIds = (witnesses: Witness[]): Witness[] => {
  return witnesses.filter(witness => {
    const validation = validateWitnessId(witness.id)
    return validation.isValid && validation.reason === 'Legacy format - requires migration'
  })
}

export const migrateWitnessId = (witness: Witness, bookId: string, chapterId: string): Witness => {
  const parsed = parseWitnessId(witness.id)
  if (!parsed.isLegacy) {
    return witness // Already using secure format
  }

  // Generate new secure ID
  const newId = generateWitnessId(bookId, chapterId)

  console.log(`🔒 SECURITY MIGRATION: Migrating witness ID from ${witness.id} to ${newId}`, {
    sigla: witness.sigla,
    oldId: witness.id,
    newId,
    bookId,
    chapterId
  })

  return {
    ...witness,
    id: newId,
    book_id: bookId
  }
}

// Debug logging helper is already defined above

// Chapter-witness lists memoized against the current witnesses array so
// repeated selector calls return identity-stable arrays (see getChapterWitnesses).
const chapterWitnessesCache = new WeakMap<Witness[], Map<string, Witness[]>>()

export const useBooksStore = create<BooksState>((set, get) => {
  return {
    // Initial state (will be populated from IndexedDB)
    books: [],
    chapters: [],
    witnesses: [],
    sourceObjects: [],
    currentBookId: null,
    currentChapterId: null,

    // Resume session state
    bookResumeData: new Map(),
    chapterResumeData: new Map(),
    witnessResumeSessions: new Map(),
    batchResumeSessions: new Map(),

    loading: true,
    error: null,
    dbInitialized: false,
    resumeDataLoading: false,

    // Book management
    createBook: async (name: string, description?: string, arabicTitle?: string) => {
      const book: Book = {
        id: generateSecureId(),
        name,
        arabic_title: arabicTitle,
        description,
        created_at: Date.now(),
        last_modified: Date.now(),
        chapter_order: []
      }

      debugLog('Creating new book', { bookId: book.id, name: book.name })

      try {
        // Save to IndexedDB and wait for completion
        await dbStorage.put('books', book)
        debugLog('Book created and saved to IndexedDB', { bookId: book.id })

        // Update state immediately after successful save
        set(state => ({
          books: [...state.books, book],
          error: null
        }))

        return book.id
      } catch (error) {
        console.error('Failed to save book to IndexedDB:', error)
        debugLog('Failed to save book', { bookId: book.id, error: error instanceof Error ? error.message : String(error) })
        set({ error: 'Failed to save book' })
        throw error
      }
    },

    updateBook: async (bookId: string, updates: Partial<Omit<Book, 'id' | 'created_at'>>) => {
      const book = get().getBook(bookId)
      if (!book) {
        set({ error: 'Book not found' })
        return
      }

      const updatedBook = { ...book, ...updates, last_modified: Date.now() }

      try {
        // Update IndexedDB
        await dbStorage.put('books', updatedBook)
        debugLog('Book updated in IndexedDB', { bookId })

        // Update cache immediately
        set(state => ({
          books: state.books.map(b => b.id === bookId ? updatedBook : b),
          error: null
        }))

      } catch (error) {
        console.error('Failed to update book in IndexedDB:', error)
        set({ error: 'Failed to update book' })
      }
    },

    archiveBook: async (bookId: string, archivedAt?: number) => {
      const book = get().getBook(bookId)
      if (!book) {
        set({ error: 'Book not found' })
        return
      }

      const timestamp = archivedAt ?? Date.now()
      const childChapters = get().getBookChapters(bookId)
      const updatedBook: Book = {
        ...book,
        archived: true,
        archived_at: timestamp,
        last_modified: Date.now(),
      }
      const updatedChapters: Chapter[] = childChapters.map(chapter => ({
        ...chapter,
        archived: true,
        archived_at: timestamp,
      }))

      try {
        await dbStorage.put('books', updatedBook)
        for (const chapter of updatedChapters) {
          await dbStorage.put('chapters', chapter)
        }
        const archivedChapterIds = new Set(updatedChapters.map(chapter => chapter.id))
        set(state => ({
          books: state.books.map(candidate => candidate.id === bookId ? updatedBook : candidate),
          chapters: state.chapters.map(chapter => archivedChapterIds.has(chapter.id)
            ? updatedChapters.find(updated => updated.id === chapter.id) || chapter
            : chapter),
          currentBookId: state.currentBookId === bookId ? null : state.currentBookId,
          currentChapterId: state.currentChapterId && archivedChapterIds.has(state.currentChapterId)
            ? null
            : state.currentChapterId,
          error: null,
        }))
      } catch (error) {
        console.error('Failed to archive book in IndexedDB:', error)
        set({ error: 'Failed to archive book' })
      }
    },

    restoreBook: async (bookId: string) => {
      const book = get().getBook(bookId)
      if (!book) {
        set({ error: 'Book not found' })
        return
      }

      const childChapters = get().getBookChapters(bookId)
      const restoredBook: Book = {
        ...withoutArchiveState(book),
        last_modified: Date.now(),
      }
      const restoredChapters: Chapter[] = childChapters.map(chapter => withoutArchiveState(chapter))

      try {
        await dbStorage.put('books', restoredBook)
        for (const chapter of restoredChapters) {
          await dbStorage.put('chapters', chapter)
        }
        const restoredChapterIds = new Set(restoredChapters.map(chapter => chapter.id))
        set(state => ({
          books: state.books.map(candidate => candidate.id === bookId ? restoredBook : candidate),
          chapters: state.chapters.map(chapter => restoredChapterIds.has(chapter.id)
            ? restoredChapters.find(restored => restored.id === chapter.id) || chapter
            : chapter),
          error: null,
        }))
      } catch (error) {
        console.error('Failed to restore book in IndexedDB:', error)
        set({ error: 'Failed to restore book' })
      }
    },

    updateSourceObject: async (sourceObjectId: string, updates: Partial<Omit<SourceObject, 'id' | 'work_id' | 'created_at'>>) => {
      const cached = get().sourceObjects.find(candidate => candidate.id === sourceObjectId)
      const existing = cached || await dbStorage.getSourceObject(sourceObjectId)
      if (!existing) {
        set({ error: 'Source object not found' })
        return
      }

      const updated: SourceObject = {
        ...existing,
        ...updates,
        last_modified: Date.now(),
        version: existing.version + 1
      }

      try {
        // Persist first; the cache only reflects records IndexedDB accepted
        await dbStorage.saveSourceObject(updated)
        set(state => ({
          sourceObjects: state.sourceObjects.some(candidate => candidate.id === sourceObjectId)
            ? state.sourceObjects.map(candidate => candidate.id === sourceObjectId ? updated : candidate)
            : [...state.sourceObjects, updated],
          error: null
        }))
      } catch (error) {
        console.error('Failed to update source object in IndexedDB:', error)
        set({ error: 'Failed to update source object' })
      }
    },

    deleteBook: (bookId: string) => {
      void get().deleteCascade({ kind: 'work', id: bookId }).catch(error => {
        console.error('Failed to delete book from IndexedDB:', error)
        set({ error: 'Failed to delete book' })
      })
    },

    previewDeletion: async (target: CascadeTarget) => {
      const state = get()
      const scope = buildCascadeScope(target, {
        chapters: state.chapters,
        witnesses: state.witnesses,
        sourceObjects: state.sourceObjects,
      })
      return previewCascade(scope)
    },

    deleteCascade: async (target: CascadeTarget) => {
      const state = get()
      const scope = buildCascadeScope(target, {
        chapters: state.chapters,
        witnesses: state.witnesses,
        sourceObjects: state.sourceObjects,
      })
      // Capture the parent book before rows disappear so chapter_order can
      // be repaired after a chapter-level delete.
      const parentBookId = target.kind === 'chapter'
        ? state.chapters.find(chapter => chapter.id === target.id)?.book_id
        : undefined

      const plan = await executeCascade(scope)
      debugLog('Cascade deletion executed', { target, perStore: plan.perStore })

      // Resume sessions live in separate resume storage; drop them per witness.
      for (const witnessId of Array.from(scope.witnessIds)) {
        await get().deleteWitnessResumeSession(witnessId)
      }

      set(currentState => ({
        books: currentState.books.filter(book => !scope.bookIds.has(book.id)),
        chapters: currentState.chapters.filter(chapter => !scope.chapterIds.has(chapter.id)),
        witnesses: currentState.witnesses.filter(witness => !scope.witnessIds.has(witness.id)),
        sourceObjects: currentState.sourceObjects.filter(sourceObject => !scope.sourceObjectIds.has(sourceObject.id)),
        currentBookId: currentState.currentBookId && scope.bookIds.has(currentState.currentBookId)
          ? null
          : currentState.currentBookId,
        currentChapterId: currentState.currentChapterId && scope.chapterIds.has(currentState.currentChapterId)
          ? null
          : currentState.currentChapterId,
        error: null,
      }))

      if (parentBookId) {
        const parentBook = get().getBook(parentBookId)
        if (parentBook) {
          await get().updateBook(parentBookId, {
            chapter_order: parentBook.chapter_order.filter(id => id !== target.id),
          })
        }
      }

      return plan
    },

    setCurrentBook: (bookId: string | null) => {
      set({
        currentBookId: bookId,
        currentChapterId: null // Reset current chapter when changing books
      })
    },

    reorderChapters: (bookId: string, newOrder: string[]) => {
      set(state => ({
        books: state.books.map(book =>
          book.id === bookId
            ? { ...book, chapter_order: newOrder, last_modified: Date.now() }
            : book
        )
      }))

      // Update chapter order_index based on new order
      newOrder.forEach((chapterId, index) => {
        get().updateChapter(chapterId, { order_index: index })
      })

      // Note: Data is automatically persisted to IndexedDB via updateChapter calls
    },

    // Chapter management
    createChapter: async (bookId: string, name: string, description?: string) => {
      const book = get().getBook(bookId)
      if (!book) {
        set({ error: 'Book not found' })
        throw new Error('Book not found')
      }

      const chapter: Chapter = {
        id: generateSecureId(),
        book_id: bookId,
        name,
        label_prefix: deriveContextLabelPrefix(
          name,
          get()
            .getBookChapters(bookId)
            .map(existingChapter => existingChapter.label_prefix || deriveContextLabelPrefix(existingChapter.name))
        ),
        description,
        order_index: book.chapter_order.length,
        created_at: Date.now()
      }

      debugLog('Creating chapter', { bookId, chapterId: chapter.id, name })

      try {
        // Save to IndexedDB and wait for completion
        await dbStorage.put('chapters', chapter)
        debugLog('Chapter saved to IndexedDB', { chapterId: chapter.id })

        // Update the book with new chapter order
        const updatedBook = { ...book, chapter_order: [...book.chapter_order, chapter.id], last_modified: Date.now() }
        await dbStorage.put('books', updatedBook)

        // Update cache immediately after successful saves
        set(state => ({
          chapters: [...state.chapters, chapter],
          books: state.books.map(b =>
            b.id === bookId ? updatedBook : b
          ),
          error: null
        }))

        return chapter.id
      } catch (error) {
        console.error('Failed to save chapter to IndexedDB:', error)
        debugLog('Failed to save chapter', { chapterId: chapter.id, error: error instanceof Error ? error.message : String(error) })
        set({ error: 'Failed to save chapter' })
        throw error
      }
    },

    updateChapter: async (chapterId: string, updates: Partial<Omit<Chapter, 'id' | 'book_id' | 'created_at'>>) => {
      const chapter = get().getChapter(chapterId)
      if (!chapter) {
        set({ error: 'Chapter not found' })
        return
      }

      const updatedChapter = { ...chapter, ...updates }

      try {
        // Update IndexedDB
        await dbStorage.put('chapters', updatedChapter)
        debugLog('Chapter updated in IndexedDB', { chapterId })

        // Update cache immediately
        set(state => ({
          chapters: state.chapters.map(c => c.id === chapterId ? updatedChapter : c),
          error: null
        }))

      } catch (error) {
        console.error('Failed to update chapter in IndexedDB:', error)
        set({ error: 'Failed to update chapter' })
      }
    },

    archiveChapter: async (chapterId: string, archivedAt?: number) => {
      const chapter = get().getChapter(chapterId)
      if (!chapter) {
        set({ error: 'Chapter not found' })
        return
      }

      const updatedChapter: Chapter = {
        ...chapter,
        archived: true,
        archived_at: archivedAt ?? Date.now(),
      }

      try {
        await dbStorage.put('chapters', updatedChapter)
        set(state => ({
          chapters: state.chapters.map(candidate => candidate.id === chapterId ? updatedChapter : candidate),
          currentChapterId: state.currentChapterId === chapterId ? null : state.currentChapterId,
          error: null,
        }))
      } catch (error) {
        console.error('Failed to archive chapter in IndexedDB:', error)
        set({ error: 'Failed to archive chapter' })
      }
    },

    restoreChapter: async (chapterId: string) => {
      const chapter = get().getChapter(chapterId)
      if (!chapter) {
        set({ error: 'Chapter not found' })
        return
      }

      const restoredChapter = withoutArchiveState(chapter)
      const parentBook = get().getBook(chapter.book_id)
      const restoredBook = parentBook && !parentBook.chapter_order.includes(chapterId)
        ? { ...parentBook, chapter_order: [...parentBook.chapter_order, chapterId], last_modified: Date.now() }
        : null

      try {
        await dbStorage.putManyAcrossStores({
          chapters: [restoredChapter],
          books: restoredBook ? [restoredBook] : [],
        })
        set(state => ({
          books: restoredBook
            ? state.books.map(candidate => candidate.id === restoredBook.id ? restoredBook : candidate)
            : state.books,
          chapters: state.chapters.map(candidate => candidate.id === chapterId ? restoredChapter : candidate),
          error: null,
        }))
      } catch (error) {
        console.error('Failed to restore chapter in IndexedDB:', error)
        set({ error: 'Failed to restore chapter' })
      }
    },

    deleteChapter: (chapterId: string) => {
      void get().deleteCascade({ kind: 'chapter', id: chapterId }).catch(error => {
        console.error('Failed to delete chapter from IndexedDB:', error)
        set({ error: 'Failed to delete chapter' })
      })
    },

    setCurrentChapter: (chapterId: string | null) => {
      set({ currentChapterId: chapterId })
    },

    moveChapter: (chapterId: string, direction: 'up' | 'down') => {
      const chapter = get().getChapter(chapterId)
      if (!chapter) return

      const book = get().getBook(chapter.book_id)
      if (!book) return

      const currentIndex = book.chapter_order.indexOf(chapterId)
      if (currentIndex === -1) return

      const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1
      if (newIndex < 0 || newIndex >= book.chapter_order.length) return

      const newOrder = [...book.chapter_order]
      newOrder[currentIndex] = newOrder[newIndex]
      newOrder[newIndex] = chapterId

      get().reorderChapters(chapter.book_id, newOrder)
    },

    // Witness management - SECURITY ENHANCED with scoped IDs
    createWitness: async (chapterId: string, witnessData: Omit<Witness, 'id' | 'chapter_id' | 'book_id' | 'created_at' | 'processing_status'>) => {
      // CRITICAL SECURITY: Get chapter and book info for proper ID scoping
      const chapter = get().getChapter(chapterId)
      if (!chapter) {
        console.error('🔒 SECURITY ERROR: Cannot create witness - chapter not found:', chapterId)
        set({ error: 'Chapter not found - cannot create witness' })
        throw new Error('Chapter not found - cannot create witness')
      }

      const book = get().getBook(chapter.book_id)
      if (!book) {
        console.error('🔒 SECURITY ERROR: Cannot create witness - book not found:', chapter.book_id)
        set({ error: 'Book not found - cannot create witness' })
        throw new Error('Book not found - cannot create witness')
      }

      // Generate secure, globally unique witness ID with book/chapter scoping
      const secureWitnessId = generateWitnessId(book.id, chapter.id)
      const sourceObjectId = witnessData.source_object_id || await resolveOrCreateSourceObjectId({
        workId: book.id,
        sourceData: witnessData,
        existingSourceObjects: get().sourceObjects,
        onStoredSourceObject: (sourceObject) => {
          set(state => ({
            sourceObjects: state.sourceObjects.some(candidate => candidate.id === sourceObject.id)
              ? state.sourceObjects
              : [...state.sourceObjects, sourceObject]
          }))
        },
        onCreatedSourceObject: async (sourceObject) => {
          await dbStorage.saveSourceObject(sourceObject)
          set(state => ({
            sourceObjects: [...state.sourceObjects, sourceObject]
          }))
        }
      })

      const witness: Witness = {
        ...witnessData,
        id: secureWitnessId,
        chapter_id: chapterId,
        book_id: book.id,
        source_object_id: sourceObjectId,
        processing_status: witnessData.preprocessing_result ? 'preprocessed' : 'pending',
        created_at: Date.now(),
        last_modified: Date.now(),
        version: 1  // SYNC: Initial version for change detection
      }

      if (witness.segmentation_result) {
        witness.segmentation_result = normalizeSegmentationResult(witness.segmentation_result)
      }

      // DEBUG: Log witness creation details
      console.log('🔍 BooksStore - Creating witness:', {
        witnessId: secureWitnessId,
        sigla: witness.sigla,
        processing_status: witness.processing_status,
        has_preprocessing_result: !!witness.preprocessing_result,
        preprocessing_result_keys: witness.preprocessing_result ? Object.keys(witness.preprocessing_result) : [],
        chunks_count: witness.preprocessing_result?.chunks?.length || 0,
        secondary_chunks_count: witness.preprocessing_result?.secondary_chunking?.chunks?.length || 0
      })

      console.log('🔒 SECURITY: Creating witness with secure scoped ID', {
        witnessId: secureWitnessId,
        bookId: book.id,
        chapterId: chapter.id,
        sigla: witness.sigla,
        securityValidation: validateWitnessId(secureWitnessId)
      })

      debugLog('Creating witness', { chapterId, sigla: witness.sigla, fileSize: witness.uploaded_file.size })

      try {
        // Save to IndexedDB and wait for completion
        await dbStorage.put('witnesses', witness)
        debugLog('Witness created successfully in IndexedDB', { witnessId: witness.id })

        // Update cache immediately after successful save
        set(state => ({
          witnesses: [...state.witnesses, witness],
          error: null
        }))

        return witness.id
      } catch (error) {
        console.error('Failed to save witness to IndexedDB:', error)
        debugLog('Failed to save witness', { error: error instanceof Error ? error.message : String(error) })

        if (error instanceof Error && error.name === 'QuotaExceededError') {
          set({ error: 'Storage quota exceeded. IndexedDB limit reached (very rare - typically 50MB+).' })
        } else {
          set({ error: 'Failed to save witness. Please try again.' })
        }
        throw error
      }
    },

    ensureRecordSourceObjectLink: async (workId: string, sourceData: SourceObjectLinkInput) => {
      if (!sourceData.manuscript_record_id) {
        debugLog('ensureRecordSourceObjectLink failed - manuscript record missing', { workId, sourceData })
        set({ error: 'Manuscript record not found' })
        throw new Error('Manuscript record not found')
      }

      const sourceObjectId = await resolveOrCreateSourceObjectId({
        workId,
        sourceData,
        existingSourceObjects: get().sourceObjects,
        onStoredSourceObject: (sourceObject) => {
          set(state => ({
            sourceObjects: state.sourceObjects.some(candidate => candidate.id === sourceObject.id)
              ? state.sourceObjects
              : [...state.sourceObjects, sourceObject]
          }))
        },
        onCreatedSourceObject: async (sourceObject) => {
          await dbStorage.saveSourceObject(sourceObject)
          set(state => ({
            sourceObjects: [...state.sourceObjects, sourceObject]
          }))
        }
      })

      set({ error: null })
      return sourceObjectId
    },

    ensureWitnessSourceObjectLink: async (witnessId: string, sourceData = {}) => {
      const witness = get().getWitness(witnessId)
      if (!witness) {
        debugLog('ensureWitnessSourceObjectLink failed - witness not found', { witnessId })
        set({ error: 'Witness not found' })
        throw new Error('Witness not found')
      }

      if (witness.source_object_id) {
        return witness.source_object_id
      }

      const sourceObjectId = await resolveOrCreateSourceObjectId({
        workId: witness.book_id,
        sourceData: {
          sigla: sourceData.sigla ?? witness.sigla,
          description: sourceData.description ?? witness.description,
          manuscript_date: sourceData.manuscript_date ?? witness.manuscript_date,
          library: sourceData.library ?? witness.library,
          city: sourceData.city ?? witness.city,
          shelfmark: sourceData.shelfmark ?? witness.shelfmark,
          witness_type: sourceData.witness_type ?? witness.witness_type
        },
        existingSourceObjects: get().sourceObjects,
        onStoredSourceObject: (sourceObject) => {
          set(state => ({
            sourceObjects: state.sourceObjects.some(candidate => candidate.id === sourceObject.id)
              ? state.sourceObjects
              : [...state.sourceObjects, sourceObject]
          }))
        },
        onCreatedSourceObject: async (sourceObject) => {
          await dbStorage.saveSourceObject(sourceObject)
          set(state => ({
            sourceObjects: [...state.sourceObjects, sourceObject]
          }))
        }
      })

      const updatedWitness: Witness = {
        ...witness,
        source_object_id: sourceObjectId,
        last_modified: Date.now(),
        version: (witness.version || 0) + 1
      }

      await dbStorage.put('witnesses', updatedWitness)
      set(state => ({
        witnesses: state.witnesses.map(candidate => candidate.id === witnessId ? updatedWitness : candidate),
        error: null
      }))

      return sourceObjectId
    },

    updateWitness: async (witnessId: string, updates: Partial<Omit<Witness, 'id' | 'chapter_id' | 'created_at'>>) => {
      const witness = get().getWitness(witnessId)
      if (!witness) {
        debugLog('updateWitness failed - witness not found', { witnessId })
        set({ error: 'Witness not found' })
        throw new Error('Witness not found')
      }

      debugLog('updateWitness called', {
        witnessId,
        updates,
        hasPreprocessingResult: !!updates.preprocessing_result,
        hasSegmentationResult: !!updates.segmentation_result,
        originalStatus: witness.processing_status,
        newStatus: updates.processing_status
      })

      // Add console log for segmentation completion tracking
      if (updates.processing_status === 'completed') {
        console.log(' booksStore.updateWitness: Updating witness to completed status', {
          witnessId,
          sigla: witness.sigla,
          hasSegmentationResult: !!updates.segmentation_result,
          updatesKeys: Object.keys(updates)
        })
      }

      // Ensure book_id is included for security isolation
      const chapter = get().getChapter(witness.chapter_id)
      const book_id = witness.book_id || chapter?.book_id
      if (!book_id) {
        console.error('🔒 SECURITY ERROR: Cannot update witness - missing book_id')
        set({ error: 'Missing book_id - security validation failed' })
        throw new Error('Missing book_id - security validation failed')
      }

      const normalizedUpdates = updates.segmentation_result
        ? { ...updates, segmentation_result: normalizeSegmentationResult(updates.segmentation_result) }
        : updates

      // Validate segmentation integrity against source text if updating segmentation_result
      if (normalizedUpdates.segmentation_result) {
        const sourceText = normalizedUpdates.uploaded_file?.content || witness.uploaded_file?.content
        validateSegmentationIntegrity(normalizedUpdates.segmentation_result, sourceText, witnessId)
      }

      const updatedWitness = {
        ...witness,
        ...normalizedUpdates,
        book_id,
        last_modified: Date.now(),
        version: (witness.version || 0) + 1  // SYNC: Increment version on update
      }

      // Update IndexedDB and await completion
      try {
        await dbStorage.put('witnesses', updatedWitness)

        debugLog('Witness updated in IndexedDB', {
          witnessId,
          hasPreprocessingResult: !!updatedWitness.preprocessing_result,
          hasSegmentationResult: !!updatedWitness.segmentation_result,
          status: updatedWitness.processing_status,
          version: updatedWitness.version
        })

        // SYNC: Emit update event for sync manager
        window.dispatchEvent(new CustomEvent('witness-updated', {
          detail: { witnessId, version: updatedWitness.version }
        }))

        // Add console log for segmentation completion tracking
        if (updatedWitness.processing_status === 'completed') {
          console.log('✅ booksStore: Witness successfully updated to completed in IndexedDB', {
            witnessId,
            sigla: updatedWitness.sigla,
            hasSegmentationResult: !!updatedWitness.segmentation_result
          })
        }

        // Update cache immediately
        set(state => ({
          witnesses: state.witnesses.map(w => w.id === witnessId ? updatedWitness : w),
          error: null
        }))

        // SYNC: Trigger sync manager for real-time updates
        try {
          await syncManager.syncWitness(witnessId)
        } catch (syncError) {
          console.warn('Sync manager failed, but update succeeded:', syncError)
        }

      } catch (error) {
        console.error('Failed to update witness in IndexedDB:', error)
        set({ error: 'Failed to update witness' })
        throw error
      }
    },

    refreshWitness: async (witnessId: string) => {
      try {
        console.log(`🔄 Refreshing witness ${witnessId} from IndexedDB`)

        // Load fresh witness data from IndexedDB
        const freshWitness = await dbStorage.get('witnesses', witnessId) as Witness | undefined

        if (!freshWitness) {
          console.error(`❌ Witness ${witnessId} not found in IndexedDB`)
          return
        }

        if (freshWitness.segmentation_result) {
          freshWitness.segmentation_result = normalizeSegmentationResult(freshWitness.segmentation_result)
        }

        // Update the store state with fresh data
        set(state => ({
          witnesses: state.witnesses.map(w => w.id === witnessId ? freshWitness : w),
          error: null
        }))

        console.log(`✅ Witness ${witnessId} refreshed from IndexedDB`, {
          sigla: freshWitness.sigla,
          hasSegmentationResult: !!freshWitness.segmentation_result,
          processingStatus: freshWitness.processing_status
        })

      } catch (error) {
        console.error(`Failed to refresh witness ${witnessId}:`, error)
        set({ error: 'Failed to refresh witness' })
      }
    },

    deleteWitness: (witnessId: string) => {
      void get().deleteCascade({ kind: 'witness', id: witnessId }).catch(error => {
        console.error('Failed to delete witness from IndexedDB:', error)
        set({ error: 'Failed to delete witness' })
      })
    },

    updateWitnessProcessingResult: async (witnessId: string, type: 'preprocessing' | 'segmentation', result: any) => {
      const updateField = type === 'preprocessing' ? 'preprocessing_result' : 'segmentation_result'

      debugLog('updateWitnessProcessingResult called', {
        witnessId,
        type,
        updateField,
        hasResult: !!result,
        resultType: typeof result
      })

      const normalizedResult = normalizeSegmentationResult(result)
      const witness = get().getWitness(witnessId)
      const witnessUpdates = buildWitnessProcessingUpdates({
        witness,
        type,
        normalizedResult
      })

      await get().updateWitness(witnessId, witnessUpdates)

      // Note: Segmentation results are now stored directly in the witness record in IndexedDB
      // No need for separate storage as IndexedDB can handle large data efficiently
    },

    updateWitnessProcessingResultsBatch: async (updates) => {
      if (!Array.isArray(updates) || updates.length === 0) {
        return
      }

      const updatedWitnesses = new Map<string, Witness>()
      const now = Date.now()

      for (const entry of updates) {
        const witnessId = entry?.witnessId
        if (!witnessId) {
          continue
        }

        const witness = get().getWitness(witnessId)
        if (!witness) {
          debugLog('updateWitnessProcessingResultsBatch failed - witness not found', { witnessId })
          set({ error: 'Witness not found' })
          throw new Error('Witness not found')
        }

        const chapter = get().getChapter(witness.chapter_id)
        const book_id = witness.book_id || chapter?.book_id
        if (!book_id) {
          console.error('🔒 SECURITY ERROR: Cannot update witness - missing book_id')
          set({ error: 'Missing book_id - security validation failed' })
          throw new Error('Missing book_id - security validation failed')
        }

        const updatedWitness = prepareWitnessProcessingResultRecord({
          witness,
          chapterBookId: book_id,
          update: entry,
          now,
        })

        updatedWitnesses.set(witnessId, updatedWitness)
      }

      if (updatedWitnesses.size === 0) {
        return
      }

      try {
        await Promise.all(
          Array.from(updatedWitnesses.values()).map(updatedWitness => dbStorage.put('witnesses', updatedWitness as any))
        )

        updatedWitnesses.forEach((updatedWitness, witnessId) => {
          window.dispatchEvent(new CustomEvent('witness-updated', {
            detail: { witnessId, version: updatedWitness.version }
          }))
        })

        set(state => ({
          witnesses: state.witnesses.map(w => updatedWitnesses.get(w.id) || w),
          error: null
        }))
      } catch (error) {
        console.error('Failed to batch update witnesses in IndexedDB:', error)
        set({ error: 'Failed to update witness' })
        throw error
      }
    },

    mergeWitnessSegmentationResult: async (witnessId: string, partialResult: CompleteMicroSegmentationResult) => {
      const witness = get().getWitness(witnessId)
      if (!witness) {
        throw new Error('Witness not found')
      }

      if (!partialResult || !Array.isArray(partialResult.chunk_results)) {
        console.warn('mergeWitnessSegmentationResult: No chunk results provided, skipping merge.')
        return
      }

      const existingResult = witness.segmentation_result

      if (!existingResult || !Array.isArray(existingResult.chunk_results)) {
        await get().updateWitnessProcessingResult(witnessId, 'segmentation', partialResult)
        return
      }

      const mergedSegmentation = mergeSegmentationResults(existingResult, partialResult)

      const updates: Partial<Witness> = {
        segmentation_result: mergedSegmentation
      }

      const activeRunId = witness.active_segmentation_run
      if (activeRunId && witness.segmentation_runs && witness.segmentation_runs[activeRunId]) {
        const activeRun = witness.segmentation_runs[activeRunId]
        const mergedRunResult = mergeSegmentationResults(activeRun.result, partialResult)
        updates.segmentation_runs = {
          ...witness.segmentation_runs,
          [activeRunId]: {
            ...activeRun,
            result: mergedRunResult,
            totalChunks: mergedRunResult?.chunk_results?.length || activeRun.totalChunks,
            totalUnits: mergedRunResult?.chunk_results?.reduce((sum: number, chunk: any) => sum + (Array.isArray(chunk.micro_units) ? chunk.micro_units.length : 0), 0) || activeRun.totalUnits
          }
        }
      }

      await get().updateWitness(witnessId, updates)
      await syncWitnessSegmentationToAnchoredRows({
        chapterId: witness.chapter_id,
        witnessId,
        previousSegmentationResult: existingResult,
        nextSegmentationResult: mergedSegmentation
      })
    },

    updateWitnessScenes: async (witnessId: string, sceneLayer: SceneLayer) => {
      const witness = get().getWitness(witnessId)
      if (!witness || !witness.segmentation_result) {
        console.warn('updateWitnessScenes: Witness or segmentation result not found', { witnessId })
        return
      }

      const totalScenes = Array.isArray(sceneLayer?.scene_order) && sceneLayer.scene_order.length > 0
        ? sceneLayer.scene_order.length
        : Array.isArray(sceneLayer?.scene_map)
          ? sceneLayer.scene_map.length
          : 0

      const baseResult = {
        ...witness.segmentation_result,
        scenes: sceneLayer,
        aggregated_statistics: {
          ...witness.segmentation_result.aggregated_statistics,
          total_scenes: totalScenes
        }
      }

      const normalizedResult = normalizeSegmentationResult(baseResult)

      const updates: Partial<Witness> = {
        segmentation_result: normalizedResult
      }

      const activeRunId = witness.active_segmentation_run
      if (activeRunId && witness.segmentation_runs && witness.segmentation_runs[activeRunId]) {
        const activeRun = witness.segmentation_runs[activeRunId]
        const updatedRunResult = activeRun.result
          ? {
            ...activeRun.result,
            scenes: sceneLayer,
            aggregated_statistics: {
              ...activeRun.result.aggregated_statistics,
              total_scenes: totalScenes
            }
          }
          : undefined

        updates.segmentation_runs = {
          ...witness.segmentation_runs,
          [activeRunId]: updatedRunResult
            ? {
              ...activeRun,
              result: updatedRunResult
            }
            : activeRun
        }
      }

      await get().updateWitness(witnessId, updates)
    },

    updateWitnessChunkResult: async (witnessId: string, chunkId: string, result: any) => {
      const witness = get().getWitness(witnessId)
      if (!witness) {
        console.error('🔴 updateWitnessChunkResult: Witness not found:', witnessId)
        throw new Error('Witness not found')
      }

      debugLog('updateWitnessChunkResult called', {
        witnessId,
        chunkId,
        hasResult: !!result,
        sigla: witness.sigla
      })

      // Initialize segmentation_result if it doesn't exist
      let segmentationResult = witness.segmentation_result || {
        preprocessing_source: witness.preprocessing_result?.preprocessing_metadata?.original_text_hash || witnessId,
        segmentation_metadata: {
          timestamp: new Date().toISOString(),
          session_id: `witness_${witnessId}_${Date.now()}`,
          provider: 'unknown',
          model: 'unknown',
          total_chunks: 0,
          successful_chunks: 0,
          failed_chunks: 0,
          processing_time: 0,
          llm_config: {}
        },
        chunk_results: [],
        aggregated_statistics: {
          total_units: 0,
          total_chunks: 0,
          avg_confidence: 0
        }
      }

      // Update or add the chunk result
      const existingIndex = segmentationResult.chunk_results.findIndex(
        (r: any) => r.source_chunk_id === chunkId
      )

      if (existingIndex >= 0) {
        // Update existing chunk result
        segmentationResult.chunk_results[existingIndex] = result
      } else {
        // Add new chunk result
        segmentationResult.chunk_results.push(result)
      }

      // Update aggregated statistics
      const totalUnits = segmentationResult.chunk_results.reduce(
        (sum: number, chunk: any) => sum + (chunk.micro_units?.length || 0),
        0
      )

      const totalChunks = segmentationResult.chunk_results.length

      const avgConfidence = totalChunks > 0 ? segmentationResult.chunk_results.reduce(
        (sum: number, chunk: any) => {
          const chunkAvg = chunk.micro_units?.reduce((s: number, u: any) => s + (u.confidence_score || 0), 0) || 0
          return sum + (chunk.micro_units?.length ? chunkAvg / chunk.micro_units.length : 0)
        }, 0
      ) / totalChunks : 0

      segmentationResult.aggregated_statistics = {
        total_units: totalUnits,
        total_chunks: totalChunks,
        avg_confidence: avgConfidence
      }

      // Update metadata
      segmentationResult.segmentation_metadata.successful_chunks = totalChunks
      segmentationResult.segmentation_metadata.total_chunks = totalChunks

      console.log('✅ booksStore: Incrementally saving chunk result to witness:', {
        witnessId,
        chunkId,
        totalChunks,
        totalUnits,
        sigla: witness.sigla
      })

      // Determine whether we should force the status back to `segmenting`.
      // Late-arriving chunk updates can land after final completion events,
      // which would otherwise downgrade a `completed` card to `segmenting`.
      const statusBefore = witness.processing_status
      const shouldForceSegmenting = !['completed', 'error', 'paused'].includes(statusBefore)

      const witnessUpdates: Partial<Witness> = {
        segmentation_result: normalizeSegmentationResult(segmentationResult)
      }

      if (shouldForceSegmenting) {
        witnessUpdates.processing_status = 'segmenting'
      }

      // Save the updated segmentation result to the witness. Preserve
      // terminal states (completed/error/paused) when chunk updates arrive late.
      await get().updateWitness(witnessId, witnessUpdates)
    },

    updateWitnessStatus: async (witnessId: string, status: Witness['processing_status']) => {
      debugLog('updateWitnessStatus called', { witnessId, status })
      await get().updateWitness(witnessId, { processing_status: status })
    },

    // Multiple segmentation run management
    createSegmentationRun: async (witnessId: string, runId: string, llmConfig: LLMConfig) => {
      const witness = get().getWitness(witnessId)
      if (!witness) {
        throw new Error('Witness not found')
      }

      const newRun: SegmentationRun = {
        runId,
        createdAt: Date.now(),
        llmConfig,
        totalChunks: 0,
        totalUnits: 0,
        status: 'processing',
        result: {
          preprocessing_source: witness.preprocessing_result?.preprocessing_metadata?.original_text_hash || '',
          segmentation_metadata: {},
          chunk_results: [],
          aggregated_statistics: {}
        }
      }

      const segmentationRuns = witness.segmentation_runs || {}
      segmentationRuns[runId] = newRun

      await get().updateWitness(witnessId, {
        segmentation_runs: segmentationRuns,
        active_segmentation_run: runId
      })

      debugLog('Created segmentation run', { witnessId, runId })
    },

    updateSegmentationRun: async (witnessId: string, runId: string, updates: Partial<SegmentationRun>) => {
      const witness = get().getWitness(witnessId)
      if (!witness || !witness.segmentation_runs || !witness.segmentation_runs[runId]) {
        throw new Error('Segmentation run not found')
      }

      const updatedRun = {
        ...witness.segmentation_runs[runId],
        ...updates
      }

      const segmentationRuns = {
        ...witness.segmentation_runs,
        [runId]: updatedRun
      }

      await get().updateWitness(witnessId, { segmentation_runs: segmentationRuns })
      debugLog('Updated segmentation run', { witnessId, runId, updates })
    },

    completeSegmentationRun: async (witnessId: string, runId: string, result: CompleteMicroSegmentationResult) => {
      const witness = get().getWitness(witnessId)
      if (!witness || !witness.segmentation_runs || !witness.segmentation_runs[runId]) {
        throw new Error('Segmentation run not found')
      }

      const totalUnits = result.chunk_results.reduce((sum, chunk) => sum + (chunk.micro_units?.length || 0), 0)

      await get().updateSegmentationRun(witnessId, runId, {
        completedAt: Date.now(),
        status: 'completed',
        result,
        totalChunks: result.chunk_results.length,
        totalUnits,
        processingTime: Date.now() - witness.segmentation_runs[runId].createdAt
      })

      // Also update the legacy segmentation_result for backward compatibility
      await get().updateWitness(witnessId, {
        segmentation_result: result,
        active_segmentation_run: runId
      })

      debugLog('Completed segmentation run', { witnessId, runId, totalUnits })
    },

    deleteSegmentationRun: async (witnessId: string, runId: string) => {
      const witness = get().getWitness(witnessId)
      if (!witness || !witness.segmentation_runs) {
        return
      }

      const { [runId]: deleted, ...remainingRuns } = witness.segmentation_runs

      // If deleting the active run, switch to another or undefined
      let newActiveRun = witness.active_segmentation_run
      if (witness.active_segmentation_run === runId) {
        const runIds = Object.keys(remainingRuns)
        newActiveRun = runIds.length > 0 ? runIds[runIds.length - 1] : undefined
      }

      await get().updateWitness(witnessId, {
        segmentation_runs: remainingRuns,
        active_segmentation_run: newActiveRun,
        // Update legacy segmentation_result if needed
        segmentation_result: newActiveRun && remainingRuns[newActiveRun]
          ? remainingRuns[newActiveRun].result
          : undefined
      })

      debugLog('Deleted segmentation run', { witnessId, runId })
    },

    setActiveSegmentationRun: async (witnessId: string, runId: string | undefined) => {
      const witness = get().getWitness(witnessId)
      if (!witness) {
        throw new Error('Witness not found')
      }

      if (runId && (!witness.segmentation_runs || !witness.segmentation_runs[runId])) {
        throw new Error('Segmentation run not found')
      }

      await get().updateWitness(witnessId, {
        active_segmentation_run: runId,
        // Update legacy segmentation_result for backward compatibility
        segmentation_result: runId && witness.segmentation_runs && witness.segmentation_runs[runId]
          ? witness.segmentation_runs[runId].result
          : undefined
      })

      debugLog('Set active segmentation run', { witnessId, runId })
    },

    getWitnessSegmentationRuns: (witnessId: string): SegmentationRun[] => {
      const witness = get().getWitness(witnessId)
      if (!witness || !witness.segmentation_runs) {
        return []
      }

      return Object.values(witness.segmentation_runs).sort((a, b) => b.createdAt - a.createdAt)
    },

    // Getter functions
    getBook: (bookId: string) => {
      const books = get().books
      debugLog('getBook called', { bookId, totalBooks: books.length })
      const foundBook = books.find(book => book.id === bookId)
      debugLog('getBook result', { bookId, found: !!foundBook, bookName: foundBook?.name })
      return foundBook
    },

    getChapter: (chapterId: string) => {
      const { chapters } = get()
      return chapters.find(chapter => chapter.id === chapterId)
    },

    getWitness: (witnessId: string) => {
      const { witnesses } = get()
      return witnesses.find(witness => witness.id === witnessId)
    },

    getSourceObject: (sourceObjectId: string) => {
      const { sourceObjects } = get()
      return sourceObjects.find(sourceObject => sourceObject.id === sourceObjectId)
    },

    getWorkSourceObjects: (bookId: string) => {
      const { sourceObjects } = get()
      return sourceObjects
        .filter(sourceObject => sourceObject.work_id === bookId)
        .sort((a, b) => a.canonical_label.localeCompare(b.canonical_label))
    },

    getBookChapters: (bookId: string) => {
      const { chapters } = get()
      return chapters
        .filter(chapter => chapter.book_id === bookId)
        .sort((a, b) => a.order_index - b.order_index)
    },

    getChapterWitnesses: (chapterId: string) => {
      const { witnesses } = get()
      // Memoized per witnesses-array identity so selectors receive a stable
      // array between store updates; a WeakMap entry dies with its array.
      let byChapter = chapterWitnessesCache.get(witnesses)
      if (!byChapter) {
        byChapter = new Map()
        chapterWitnessesCache.set(witnesses, byChapter)
      }
      const cached = byChapter.get(chapterId)
      if (cached) {
        return cached
      }
      const chapterWitnesses = witnesses
        .filter(witness => witness.chapter_id === chapterId)
        .sort((a, b) => a.created_at - b.created_at)
      byChapter.set(chapterId, chapterWitnesses)
      return chapterWitnesses
    },

    getCurrentBook: () => {
      const { currentBookId } = get()
      return currentBookId ? get().getBook(currentBookId) : undefined
    },

    getCurrentChapter: () => {
      const { currentChapterId } = get()
      return currentChapterId ? get().getChapter(currentChapterId) : undefined
    },

    // Note: localStorage data persistence functions removed - now using IndexedDB only

    // Import/Export functionality
    exportBook: (bookId: string) => {
      const book = get().getBook(bookId)
      if (!book) return null

      const chapters = get().getBookChapters(bookId)
      const witnesses: Witness[] = []
      const segmentationResults: Record<string, CompleteMicroSegmentationResult> = {}

      // Collect all data for the book (using cached data)
      chapters.forEach(chapter => {
        const chapterWitnesses = get().getChapterWitnesses(chapter.id)
        witnesses.push(...chapterWitnesses)

        // Collect segmentation results from witness records (now stored in IndexedDB)
        chapterWitnesses.forEach(witness => {
          if (witness.segmentation_result) {
            segmentationResults[witness.id] = witness.segmentation_result
          }
        })
      })

      // Note: Alignments would need to be fetched async from IndexedDB
      // For now, export without alignments as they require async access
      const exportData: BookExport = {
        export_type: 'scholarly_book_v1',
        export_timestamp: Date.now(),
        book,
        chapters,
        witnesses,
        segmentation_results: segmentationResults,
        alignments: [] // TODO: Fetch alignments async when needed
      }

      return exportData
    },

    importBook: async (exportData: BookExport) => {
      if (exportData.export_type !== 'scholarly_book_v1') {
        set({ error: 'Invalid export format' })
        return null
      }

      try {
        // Create new book with unique ID
        const newBookId = await get().createBook(exportData.book.name, exportData.book.description)

        // Import chapters
        const chapterIdMap: Record<string, string> = {}
        for (const chapter of exportData.chapters) {
          const newChapterId = await get().createChapter(newBookId, chapter.name, chapter.description)
          chapterIdMap[chapter.id] = newChapterId
        }

        // Import witnesses
        const witnessIdMap: Record<string, string> = {}
        for (const witness of exportData.witnesses) {
          const newChapterId = chapterIdMap[witness.chapter_id]
          if (newChapterId) {
            const newWitnessId = await get().createWitness(newChapterId, {
              sigla: witness.sigla,
              description: witness.description,
              manuscript_date: witness.manuscript_date,
              library: witness.library,
              uploaded_file: witness.uploaded_file,
              preprocessing_result: witness.preprocessing_result,
              segmentation_result: witness.segmentation_result,
              last_modified: Date.now(),
              version: 1
            })
            witnessIdMap[witness.id] = newWitnessId

            // Set processing status
            await get().updateWitnessStatus(newWitnessId, witness.processing_status)
          }
        }

        // Note: Segmentation results are now imported directly as part of witness records above
        // No need for separate localStorage storage as they're stored in the witness.segmentation_result field

        // Import alignments
        exportData.alignments.forEach(alignment => {
          const newChapterId = chapterIdMap[alignment.chapter_id]
          if (newChapterId) {
            const newWitnessIds = alignment.selected_witnesses.map(id => witnessIdMap[id]).filter(Boolean)
            if (newWitnessIds.length > 0) {
              get().createAlignmentSession(newChapterId, newWitnessIds)
            }
          }
        })

        return newBookId
      } catch (error) {
        console.error('Failed to import book:', error)
        set({ error: 'Failed to import book' })
        return null
      }
    },

    // Alignment management
    createAlignmentSession: (chapterId: string, witnessIds: string[]) => {
      const chapter = get().getChapter(chapterId)
      if (!chapter) {
        console.error('🔒 SECURITY ERROR: Cannot create alignment - chapter not found:', chapterId)
        set({ error: 'Chapter not found - cannot create alignment' })
        return ''
      }

      const session: ChapterAlignmentSession = {
        id: generateSecureId(),
        chapter_id: chapterId,
        book_id: chapter.book_id,
        selected_witnesses: witnessIds,
        created_at: Date.now()
      }

      // Save to IndexedDB
      dbStorage.put('alignments', session).then(() => {
        debugLog('Alignment session created in IndexedDB', { sessionId: session.id, chapterId })
      }).catch(error => {
        console.error('Failed to save alignment session to IndexedDB:', error)
        set({ error: 'Failed to save alignment session' })
      })

      return session.id
    },

    updateAlignmentResult: (sessionId: string, result: AlignmentResult) => {
      // Get the alignment session from IndexedDB and update it
      dbStorage.get('alignments', sessionId).then(session => {
        if (session) {
          const updatedSession = { ...session, alignment_result: result }

          dbStorage.put('alignments', updatedSession).then(() => {
            debugLog('Alignment result updated in IndexedDB', { sessionId })
          }).catch(error => {
            console.error('Failed to update alignment result in IndexedDB:', error)
            set({ error: 'Failed to update alignment result' })
          })
        }
      }).catch(error => {
        console.error('Failed to find alignment session:', error)
        set({ error: 'Alignment session not found' })
      })
    },

    getChapterAlignments: (chapterId: string) => {
      // Since alignments are not cached for synchronous access, we'll need to
      // modify this to be async or cache alignments too. For now, return empty array
      // and let components use the IndexedDB storage directly when needed
      console.warn('getChapterAlignments is synchronous but IndexedDB is async. Consider using dbStorage.getChapterAlignments() directly.')
      return []
    },

    deleteAlignmentSession: async (sessionId: string) => {
      try {
        await dbStorage.delete('alignments', sessionId)
        debugLog('Alignment session deleted from IndexedDB', { sessionId })
        return true
      } catch (error) {
        console.error('Failed to delete alignment session:', error)
        set({ error: 'Failed to delete alignment session' })
        return false
      }
    },

    // Database initialization
    initializeDB: async () => {
      try {
        set({ loading: true, error: null })

        await initializeDB()

        // Load all data from IndexedDB into cache
        const [books, chapters, loadedWitnesses, loadedSourceObjects] = await Promise.all([
          dbStorage.getAll('books'),
          dbStorage.getAll('chapters'),
          dbStorage.getAllWitnessMetadata() as Promise<Witness[]>,
          dbStorage.getSourceObjects()
        ])

        const {
          witnesses,
          sourceObjects,
          report: sourceObjectBackfillReport
        } = await backfillWitnessSourceObjects({
          witnesses: loadedWitnesses,
          sourceObjects: loadedSourceObjects
        })

        console.log('🔍 BooksStore - Database initialized, loaded data:', {
          books: books.length,
          chapters: chapters.length,
          witnesses: witnesses.length,
          sourceObjects: sourceObjects.length,
          witnessIds: witnesses.map(w => w.id),
          witnessesWithPreprocessing: witnesses.filter(w => w.preprocessing_result).length
        })

        set({
          books,
          chapters,
          witnesses,
          sourceObjects,
          loading: false,
          dbInitialized: true
        })

        debugLog('IndexedDB initialized and all data loaded', {
          books: books.length,
          chapters: chapters.length,
          witnesses: witnesses.length,
          sourceObjects: sourceObjects.length
        })

        if (
          sourceObjectBackfillReport.linkedWitnesses > 0 ||
          sourceObjectBackfillReport.repairedWitnesses > 0 ||
          sourceObjectBackfillReport.createdSourceObjects > 0
        ) {
          debugLog('Backfilled witness source-object links', sourceObjectBackfillReport)
        }

        // CRITICAL SECURITY: Auto-detect and log legacy witness IDs for migration
        const legacyWitnesses = detectLegacyWitnessIds(witnesses)
        if (legacyWitnesses.length > 0) {
          console.warn(`🔒 SECURITY ALERT: Found ${legacyWitnesses.length} witnesses with legacy IDs that need migration:`)
          legacyWitnesses.forEach(witness => {
            console.warn(`  - ${witness.sigla} (ID: ${witness.id})`)
          })
          console.warn('🔒 Run store.migrateAllLegacyWitnessIds() to fix this security vulnerability')
        } else {
          console.log('🔒 SECURITY: All witness IDs are using secure format ✓')
        }
      } catch (error) {
        console.error('Failed to initialize IndexedDB:', error)
        set({
          error: 'Failed to initialize database',
          loading: false,
          dbInitialized: false
        })
      }
    },

    // Storage management functions
    getStorageUsage: async () => {
      return await dbStorage.getStorageUsage()
    },

    clearAllData: async () => {
      try {
        await dbStorage.clearAll()

        // Reset store state
        set({
          books: [],
          chapters: [],
          witnesses: [],
          sourceObjects: [],
          currentBookId: null,
          currentChapterId: null,
          loading: false,
          error: null
        })

        debugLog('All IndexedDB data cleared', {})
      } catch (error) {
        console.error('Failed to clear data:', error)
        set({ error: 'Failed to clear data' })
      }
    },

    migrateFromLocalStorage: async () => {
      try {
        const result = await dbStorage.migrateFromLocalStorage()

        if (result.success) {
          // Reload all data after migration
          const [books, chapters, witnesses] = await Promise.all([
            dbStorage.getAll('books'),
            dbStorage.getAll('chapters'),
            dbStorage.getAll('witnesses') as Promise<Witness[]>
          ])
          set({ books, chapters, witnesses })
          debugLog('Migration completed successfully', { migrated: result.migrated })
        } else {
          console.error('Migration errors:', result.errors)
          set({ error: 'Migration completed with errors. Check console for details.' })
        }

        return result
      } catch (error) {
        console.error('Migration failed:', error)
        set({ error: 'Failed to migrate data from localStorage' })
        return { success: false, migrated: 0, errors: [String(error)] }
      }
    },

    // ============================================================================
    // Resume Session Management
    // ============================================================================

    // Load resume data functions
    loadBookResumeData: async (bookId: string) => {
      try {
        set({ resumeDataLoading: true })
        const resumeData = await resumeStorage.getBookResumeData(bookId)

        if (resumeData) {
          const newBookResumeData = new Map(get().bookResumeData)
          newBookResumeData.set(bookId, resumeData)
          set({ bookResumeData: newBookResumeData })

          debugLog('Book resume data loaded', { bookId, sessions: resumeData.total_resumable_sessions })
        }
      } catch (error) {
        console.error('Failed to load book resume data:', error)
        set({ error: 'Failed to load resume data' })
      } finally {
        set({ resumeDataLoading: false })
      }
    },

    loadChapterResumeData: async (chapterId: string) => {
      try {
        set({ resumeDataLoading: true })
        const resumeData = await resumeStorage.getChapterResumeData(chapterId)

        if (resumeData) {
          const newChapterResumeData = new Map(get().chapterResumeData)
          newChapterResumeData.set(chapterId, resumeData)
          set({ chapterResumeData: newChapterResumeData })

          debugLog('Chapter resume data loaded', { chapterId, sessions: resumeData.total_resumable_sessions })
        }
      } catch (error) {
        console.error('Failed to load chapter resume data:', error)
        set({ error: 'Failed to load chapter resume data' })
      } finally {
        set({ resumeDataLoading: false })
      }
    },

    loadWitnessResumeSession: async (witnessId: string) => {
      try {
        const session = await resumeStorage.getWitnessResumeSession(witnessId)

        if (session) {
          const newWitnessSessions = new Map(get().witnessResumeSessions)
          newWitnessSessions.set(witnessId, session)
          set({ witnessResumeSessions: newWitnessSessions })

          debugLog('Witness resume session loaded', { witnessId, completion: session.completion_percentage })
        }
      } catch (error) {
        console.error('Failed to load witness resume session:', error)
        set({ error: 'Failed to load witness resume session' })
      }
    },

    loadBatchResumeSession: async (batchId: string) => {
      try {
        const session = await resumeStorage.getBatchResumeSession(batchId)

        if (session) {
          const newBatchSessions = new Map(get().batchResumeSessions)
          newBatchSessions.set(batchId, session)
          set({ batchResumeSessions: newBatchSessions })

          debugLog('Batch resume session loaded', { batchId, totalFiles: session.total_files })
        }
      } catch (error) {
        console.error('Failed to load batch resume session:', error)
        set({ error: 'Failed to load batch resume session' })
      }
    },

    // Resume session getters
    getBookResumeData: (bookId: string) => {
      return get().bookResumeData.get(bookId) || null
    },

    getChapterResumeData: (chapterId: string) => {
      return get().chapterResumeData.get(chapterId) || null
    },

    getWitnessResumeSession: (witnessId: string) => {
      return get().witnessResumeSessions.get(witnessId) || null
    },

    getBatchResumeSession: (batchId: string) => {
      return get().batchResumeSessions.get(batchId) || null
    },

    // Resume session actions
    updateWitnessResumeSession: async (session: WitnessResumeSession) => {
      try {
        await resumeStorage.saveWitnessResumeSession(session)

        // Update local cache
        const newWitnessSessions = new Map(get().witnessResumeSessions)
        newWitnessSessions.set(session.witness_id, session)
        set({ witnessResumeSessions: newWitnessSessions })

        // Refresh related resume data
        await get().loadChapterResumeData(session.chapter_id)
        await get().loadBookResumeData(session.book_id)

        debugLog('Witness resume session updated', { witnessId: session.witness_id })
      } catch (error) {
        console.error('Failed to update witness resume session:', error)
        set({ error: 'Failed to save resume session' })
      }
    },

    updateBatchResumeSession: async (session: BatchResumeSession) => {
      try {
        await resumeStorage.saveBatchResumeSession(session)

        // Update local cache
        const newBatchSessions = new Map(get().batchResumeSessions)
        newBatchSessions.set(session.batch_id, session)
        set({ batchResumeSessions: newBatchSessions })

        debugLog('Batch resume session updated', { batchId: session.batch_id })
      } catch (error) {
        console.error('Failed to update batch resume session:', error)
        set({ error: 'Failed to save batch resume session' })
      }
    },

    deleteWitnessResumeSession: async (witnessId: string) => {
      try {
        const session = get().witnessResumeSessions.get(witnessId)
        if (!session) return

        await resumeStorage.deleteWitnessResumeSession(session.persistent_id)

        // Update local cache
        const newWitnessSessions = new Map(get().witnessResumeSessions)
        newWitnessSessions.delete(witnessId)
        set({ witnessResumeSessions: newWitnessSessions })

        // Refresh related resume data
        await get().loadChapterResumeData(session.chapter_id)
        await get().loadBookResumeData(session.book_id)

        debugLog('Witness resume session deleted', { witnessId })
      } catch (error) {
        console.error('Failed to delete witness resume session:', error)
        set({ error: 'Failed to delete resume session' })
      }
    },

    deleteBatchResumeSession: async (batchId: string) => {
      try {
        const session = get().batchResumeSessions.get(batchId)
        if (!session) return

        // Delete batch session (implementation needed in resumeStorage)

        // Update local cache
        const newBatchSessions = new Map(get().batchResumeSessions)
        newBatchSessions.delete(batchId)
        set({ batchResumeSessions: newBatchSessions })

        debugLog('Batch resume session deleted', { batchId })
      } catch (error) {
        console.error('Failed to delete batch resume session:', error)
        set({ error: 'Failed to delete batch resume session' })
      }
    },

    // Resume utilities
    refreshResumeData: async () => {
      try {
        set({ resumeDataLoading: true })

        const state = get()

        // Reload all resume data for current context
        if (state.currentBookId) {
          await get().loadBookResumeData(state.currentBookId)
        }

        if (state.currentChapterId) {
          await get().loadChapterResumeData(state.currentChapterId)
        }

        // Reload all witness sessions for current chapter
        if (state.currentChapterId) {
          const witnesses = get().getChapterWitnesses(state.currentChapterId)
          await Promise.all(
            witnesses.map(witness => get().loadWitnessResumeSession(witness.id))
          )
        }

        debugLog('Resume data refreshed')
      } catch (error) {
        console.error('Failed to refresh resume data:', error)
        set({ error: 'Failed to refresh resume data' })
      } finally {
        set({ resumeDataLoading: false })
      }
    },

    cleanupExpiredResumeSessions: async () => {
      try {
        await resumeStorage.cleanupExpiredSessions()

        // Refresh all resume data after cleanup
        await get().refreshResumeData()

        debugLog('Expired resume sessions cleaned up')
      } catch (error) {
        console.error('Failed to cleanup expired sessions:', error)
        set({ error: 'Failed to cleanup expired sessions' })
      }
    },

    getExpiringSessions: async (daysUntilExpiry: number = 3) => {
      try {
        return await resumeStorage.getExpiringSessions(daysUntilExpiry)
      } catch (error) {
        console.error('Failed to get expiring sessions:', error)
        return []
      }
    },

    resumeAllSessions: async (bookId: string) => {
      try {
        set({ resumeDataLoading: true, error: null })

        // Load book resume data
        await get().loadBookResumeData(bookId)
        const bookResumeData = get().getBookResumeData(bookId)

        if (!bookResumeData || bookResumeData.total_resumable_sessions === 0) {
          debugLog('No resumable sessions found for book:', bookId)
          return
        }

        // Resume all witness sessions across all chapters
        const resumePromises: Promise<void>[] = []

        for (const [chapterId, chapterData] of Object.entries(bookResumeData.resume_sessions || {})) {
          // Resume witness sessions
          for (const [witnessId, witnessSession] of Object.entries(chapterData.witness_sessions || {})) {
            if (witnessSession.can_resume) {
              // Create a promise to resume with original configuration
              const resumePromise = async () => {
                try {
                  // This would be handled by the segmentation store
                  debugLog(`Resuming witness session: ${witnessId} (${witnessSession.persistent_id})`)

                  // In a full implementation, this would call the segmentation store
                  // await useSegmentationStore.getState().resumeWithOriginalConfig(witnessId)

                } catch (error) {
                  console.error(`Failed to resume witness ${witnessId}:`, error)
                }
              }

              resumePromises.push(resumePromise())
            }
          }

          // Resume batch sessions
          for (const [batchId, batchSession] of Object.entries(chapterData.batch_sessions || {})) {
            if (batchSession.can_resume) {
              const resumePromise = async () => {
                try {
                  debugLog(`Resuming batch session: ${batchId} (${batchSession.persistent_id})`)

                  // In a full implementation, this would call the segmentation store
                  // await useSegmentationStore.getState().resumeBatchProcessingWithOptions(batchId, defaultOptions)

                } catch (error) {
                  console.error(`Failed to resume batch ${batchId}:`, error)
                }
              }

              resumePromises.push(resumePromise())
            }
          }
        }

        // Execute all resume operations
        await Promise.allSettled(resumePromises)

        // Refresh resume data after resuming
        await get().refreshResumeData()

        debugLog('All sessions resumed for book:', bookId)
      } catch (error) {
        console.error('Failed to resume all sessions:', error)
        set({ error: 'Failed to resume all sessions' })
      } finally {
        set({ resumeDataLoading: false })
      }
    },

    // ============================================================================
    // SECURITY: ID MIGRATION AND VALIDATION FUNCTIONS
    // ============================================================================

    detectLegacyWitnesses: () => {
      const { witnesses } = get()
      return detectLegacyWitnessIds(witnesses)
    },

    migrateAllLegacyWitnessIds: async () => {
      try {
        const { witnesses, books, chapters } = get()
        const legacyWitnesses = detectLegacyWitnessIds(witnesses)

        if (legacyWitnesses.length === 0) {
          console.log('🔒 SECURITY: No legacy witness IDs found - migration not needed')
          return { migrated: 0, errors: [] }
        }

        console.log(`🔒 SECURITY MIGRATION: Starting migration of ${legacyWitnesses.length} legacy witness IDs`)

        const errors: string[] = []
        let migrated = 0

        for (const witness of legacyWitnesses) {
          try {
            const chapter = chapters.find(c => c.id === witness.chapter_id)
            if (!chapter) {
              errors.push(`Chapter not found for witness ${witness.sigla}`)
              continue
            }

            const book = books.find(b => b.id === chapter.book_id)
            if (!book) {
              errors.push(`Book not found for witness ${witness.sigla}`)
              continue
            }

            // Migrate the witness
            const migratedWitness = migrateWitnessId(witness, book.id, chapter.id)

            // Update in IndexedDB with new ID
            await dbStorage.delete('witnesses', witness.id) // Remove old
            await dbStorage.put('witnesses', migratedWitness) // Add with new ID

            // Update in memory cache
            set(state => ({
              witnesses: state.witnesses.map(w =>
                w.id === witness.id ? migratedWitness : w
              )
            }))

            migrated++
            console.log(`🔒 SECURITY: Successfully migrated witness ${witness.sigla}`)

          } catch (error) {
            const errorMsg = `Failed to migrate witness ${witness.sigla}: ${error}`
            errors.push(errorMsg)
            console.error(errorMsg)
          }
        }

        console.log(`🔒 SECURITY MIGRATION COMPLETE: ${migrated} witnesses migrated, ${errors.length} errors`)
        return { migrated, errors }

      } catch (error) {
        console.error('🔒 SECURITY MIGRATION FAILED:', error)
        return { migrated: 0, errors: ['Migration process failed: ' + String(error)] }
      }
    },

    validateAllWitnessIds: () => {
      const { witnesses } = get()
      let valid = 0
      let invalid = 0
      let legacy = 0
      const details: any[] = []

      witnesses.forEach(witness => {
        const validation = validateWitnessId(witness.id)
        const parsed = parseWitnessId(witness.id)

        if (!validation.isValid) {
          invalid++
          details.push({
            witnessId: witness.id,
            sigla: witness.sigla,
            status: 'invalid',
            reason: validation.reason
          })
        } else if (parsed.isLegacy) {
          legacy++
          details.push({
            witnessId: witness.id,
            sigla: witness.sigla,
            status: 'legacy',
            reason: 'Requires migration to secure format'
          })
        } else {
          valid++
          details.push({
            witnessId: witness.id,
            sigla: witness.sigla,
            status: 'valid',
            bookId: parsed.bookId,
            chapterId: parsed.chapterId,
            uuid: parsed.uuid
          })
        }
      })

      console.log('🔒 SECURITY VALIDATION REPORT:', {
        total: witnesses.length,
        valid,
        invalid,
        legacy,
        percentageSecure: Math.round((valid / witnesses.length) * 100) || 0
      })

      return { valid, invalid, legacy, details }
    },

    // Load chapter data for sync manager
    loadChapterData: async (book_id: string, chapter_id: string) => {
      const { loadWitnesses } = get()
      try {
        console.log(`[BooksStore] Loading chapter data for chapter ${chapter_id} in book ${book_id}`)
        await loadWitnesses(chapter_id)
      } catch (error) {
        console.error('Failed to load chapter data:', error)
      }
    },

    // Load witnesses for a chapter 
    loadWitnesses: async (chapter_id: string) => {
      try {
        // Fetch only this chapter's records via the chapter_id index instead
        // of deserializing every witness payload in the database.
        const chapterWitnesses = await dbStorage.getAllByIndex(
          'witnesses',
          'chapter_id',
          chapter_id
        ) as Witness[]

        // Update state with witnesses
        set(state => ({
          witnesses: [
            ...state.witnesses.filter(w => w.chapter_id !== chapter_id),
            ...chapterWitnesses
          ]
        }))

        debugLog(`Loaded ${chapterWitnesses.length} witnesses for chapter ${chapter_id}`)
      } catch (error) {
        console.error('Failed to load witnesses:', error)
      }
    },

    // Add WebSocket connection properties
    processingConnections: {} as Record<string, WebSocket>,
    editingConnections: {} as Record<string, WebSocket>
  }
})
